import {
  ConfigChange,
  RESOURCE_FIELDS,
  ResourceField,
  ServiceValues,
  environmentsWith,
  resourceValue,
  workloadValuesFor,
} from '@internal/backstage-plugin-release-common';

/**
 * What the operator has typed but not yet sent: one entry per cell, keyed by
 * where the cell is. Pure, so the page can derive the changes to review and
 * the tables can look up their own cells without either knowing the other.
 */
export type Draft = Record<string, string>;

export type Cell =
  | { environment: string; workload: string; kind: 'configMap'; key: string }
  | { environment: string; workload: string; kind: 'replicaCount' }
  | { environment: string; workload: string; kind: 'resource'; field: ResourceField };

function cellName(cell: Cell): string {
  if (cell.kind === 'configMap') return cell.key;
  if (cell.kind === 'resource') return cell.field;
  return '';
}

export function cellKey(cell: Cell): string {
  return [cell.environment, cell.workload, cell.kind, cellName(cell)].join('|');
}

/** What the file holds for a cell, or undefined when it holds nothing. */
export function currentValue(values: ServiceValues, cell: Cell): string | undefined {
  const workload = workloadValuesFor(values, cell.environment, cell.workload);
  if (!workload) return undefined;
  switch (cell.kind) {
    case 'configMap':
      return workload.configMap[cell.key]?.value;
    case 'replicaCount':
      return workload.replicaCount === null ? undefined : String(workload.replicaCount);
    case 'resource':
      return resourceValue(workload.resources, cell.field);
    default:
      return undefined;
  }
}

function toChange(cell: Cell, value: string): ConfigChange {
  switch (cell.kind) {
    case 'configMap':
      return { ...cell, value };
    case 'replicaCount':
      return { ...cell, value: Number(value) };
    case 'resource':
      return { ...cell, value };
    default:
      throw new Error('unknown cell');
  }
}

/**
 * The changes a draft amounts to: every drafted cell whose value differs from
 * the file. A cell typed and then typed back to what it was is not a change,
 * and an empty draft for a key the file does not have is not one either — a
 * new key with no value is a key nobody has finished adding.
 */
export function pendingChanges(
  values: ServiceValues,
  draft: Draft,
  newKeys: Record<string, string[]>,
): ConfigChange[] {
  const changes: ConfigChange[] = [];
  const cells: Cell[] = [];
  for (const workload of new Set(values.workloads.map(w => w.workload))) {
    const environments = environmentsWith(values, workload);
    // Every key any environment has, plus the new ones: a key missing from
    // staging is exactly the cell someone types into.
    const keys = new Set<string>(newKeys[workload] ?? []);
    for (const environment of environments) {
      for (const key of Object.keys(workloadValuesFor(values, environment, workload)!.configMap)) {
        keys.add(key);
      }
    }
    for (const environment of environments) {
      for (const key of keys) {
        cells.push({ environment, workload, kind: 'configMap', key });
      }
      cells.push({ environment, workload, kind: 'replicaCount' });
      for (const field of RESOURCE_FIELDS) {
        cells.push({ environment, workload, kind: 'resource', field });
      }
    }
  }
  const seen = new Set<string>();
  for (const cell of cells) {
    const id = cellKey(cell);
    if (seen.has(id)) continue;
    seen.add(id);
    const drafted = draft[id];
    if (drafted === undefined) continue;
    const current = currentValue(values, cell);
    if (drafted === current) continue;
    if (current === undefined && drafted === '' ) continue;
    changes.push(toChange(cell, drafted));
  }
  return changes;
}
